// Hold/timed duration is stored as a single `DraftSet.duration` (seconds,
// as a STRING). The h/min/s cells of the timed logger speak numbers, so
// this module is the string adapter over the shared duration math in
// @jackedlogs/shared: it owns the two things that are genuinely local —
// parsing the stored string, and the empty-string-when-zero convention the
// save path expects — and delegates the arithmetic, which is the same
// arithmetic the cardio duration field has always used.
//
// Its one consumer today is FocusPanelTimed. The card-logger TimedSetRow
// that this header used to cite as the second one no longer exists, so
// what looked like a shared helper was a local copy of shared maths.

import { joinCardioDuration, splitCardioDuration } from '@jackedlogs/shared';

export const decomposeHold = (
  durationStr: string,
): { h: number; m: number; s: number } =>
  // parseInt on garbage gives NaN, which splitCardioDuration already
  // splits to all-zero — same result the local copy produced.
  splitCardioDuration(parseInt(durationStr, 10));

// Recompose the three cells into a seconds string (carry-normalised, so
// min 75 → 1h 15m next render). Empty string when zero so the placeholder
// shows — matches the omit-vs-zero convention the save path expects.
export const recomposeHold = (h: number, m: number, s: number): string => {
  // joinCardioDuration floors each part on top of clamping it at 0; the
  // local copy only clamped. Cells always arrive as non-negative ints
  // (holdCellNum, or decomposeHold's own output), so the two agree — and
  // where they wouldn't, flooring is the safer of the two.
  const total = joinCardioDuration(h, m, s);
  return total > 0 ? String(total) : '';
};

// Parse a single cell's text → a non-negative int (NaN/garbage → 0).
export const holdCellNum = (txt: string): number => {
  const n = parseInt(txt, 10);
  return Number.isFinite(n) && n >= 0 ? n : 0;
};
